import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Phone, CalendarDays, ArrowRight } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => `$${Number(n || 0).toLocaleString()}`;

// Inject raw embed code (chat / orb widget scripts) safely into the page
const EmbedBlock = ({ code }) => {
  const ref = React.useRef(null);
  useEffect(() => {
    if (!ref.current || !code) return;
    const slot = ref.current;
    const range = document.createRange();
    range.selectNode(slot);
    const frag = range.createContextualFragment(code);
    slot.appendChild(frag);
    return () => { slot.innerHTML = ''; };
  }, [code]);
  return <div ref={ref} />;
};

export default function PublicProposalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/lead-proposals/${token}`)
      .then(r => setData(r.data))
      .catch(() => setError('This proposal link is invalid or has been removed.'));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#f7f7f5] flex items-center justify-center p-6">
        <p className="text-gray-500 text-sm" data-testid="proposal-error">{error}</p>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#f7f7f5] flex items-center justify-center">
      <div className="w-8 h-8 rounded-full border-2 border-teal-500 border-t-transparent animate-spin" />
    </div>;
  }

  const s = data.sections || {};
  const gaps = data.gaps || [];
  const inv = data.investment || {};
  const emb = data.embeds || {};
  const total = data.total_monthly_leak_usd || 0;
  const maxLoss = Math.max(...gaps.map(g => Number(g.est_monthly_loss_usd) || 0), 1);

  return (
    <div className="min-h-screen bg-[#f7f7f5] text-[#141821]" style={{ fontFamily: "'Geist', 'Inter', system-ui, sans-serif" }}
      data-testid="public-proposal-page">
      {/* Header */}
      <header className="border-b border-black/5 bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          {data.agency?.logo_data
            ? <img src={data.agency.logo_data} alt={data.agency.name} className="h-8 w-auto object-contain" />
            : <span className="font-bold text-sm tracking-wide">{data.agency?.name || 'Growth Proposal'}</span>}
          {emb.calendar_url && (
            <button onClick={() => document.getElementById('book')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-xs font-semibold px-4 py-2 rounded-full bg-[#141821] text-white hover:bg-black transition-colors"
              data-testid="proposal-header-book-btn">
              Book a call
            </button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-24">
        {/* Hero */}
        <section className="pt-14 pb-10">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-700 mb-3">
            Prepared for {data.owner_name || data.business_name} · {data.city}
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight tracking-tight" data-testid="proposal-headline">
            {s.headline || `Growth proposal for ${data.business_name}`}
          </h1>
          {s.intro && <p className="text-[15px] text-gray-600 leading-relaxed mt-5 max-w-2xl">{s.intro}</p>}
        </section>

        {/* Leak total banner */}
        <section className="rounded-2xl bg-[#141821] text-white p-7 mb-10" data-testid="proposal-leak-banner">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-semibold">Estimated revenue leaking every month</p>
              <p className="text-4xl sm:text-5xl font-bold mt-2 text-red-400">{fmt(total)}<span className="text-lg text-white/40 font-medium">/mo</span></p>
            </div>
            <div className="text-right">
              <p className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-semibold">Leak score</p>
              <p className="text-3xl font-bold mt-1">{data.leak_score}<span className="text-sm text-white/40">/100</span></p>
            </div>
          </div>
          {s.leak_summary && <p className="text-sm text-white/70 leading-relaxed mt-5 max-w-2xl">{s.leak_summary}</p>}
        </section>

        {/* Gaps table + bars */}
        <section className="mb-12" data-testid="proposal-gaps">
          <h2 className="text-lg font-bold mb-5">Where the money is leaking</h2>
          <div className="rounded-2xl border border-black/8 bg-white overflow-hidden">
            {gaps.map((g, i) => {
              const loss = Number(g.est_monthly_loss_usd) || 0;
              return (
                <div key={i} className={`px-6 py-4 ${i > 0 ? 'border-t border-black/5' : ''}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">{g.title}</p>
                      <p className="text-[13px] text-gray-500 mt-1 leading-relaxed">{g.why}</p>
                    </div>
                    <p className="text-sm font-bold text-red-500 shrink-0">{fmt(loss)}<span className="text-[11px] text-gray-400 font-medium">/mo</span></p>
                  </div>
                  <div className="h-1.5 mt-3 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-red-400 to-red-500"
                      style={{ width: `${Math.max(6, (loss / maxLoss) * 100)}%` }} />
                  </div>
                </div>
              );
            })}
            <div className="px-6 py-4 border-t border-black/10 bg-gray-50 flex items-center justify-between">
              <p className="text-sm font-bold">Total estimated leak</p>
              <p className="text-base font-bold text-red-500">{fmt(total)}/mo</p>
            </div>
          </div>
        </section>

        {/* Solutions */}
        {(s.solutions || []).length > 0 && (
          <section className="mb-12" data-testid="proposal-solutions">
            <h2 className="text-lg font-bold mb-2">How we fix it</h2>
            {s.solution_intro && <p className="text-sm text-gray-600 mb-5 max-w-2xl">{s.solution_intro}</p>}
            <div className="grid sm:grid-cols-2 gap-3">
              {s.solutions.map((sol, i) => (
                <div key={i} className="rounded-2xl border border-black/8 bg-white p-5">
                  <p className="text-sm font-semibold">{sol.title}</p>
                  <p className="text-[13px] text-gray-500 mt-1.5 leading-relaxed">{sol.desc}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Live demo embeds */}
        {(emb.chat_code || emb.orb_code || emb.phone_number) && (
          <section className="mb-16 py-6 text-center" data-testid="proposal-demo">
            <h2 className="text-xl font-bold mb-3">Try the AI yourself — right now</h2>
            <p className="text-sm text-gray-600 mb-12">This is the exact experience your customers would get.</p>
            {emb.orb_code && (
              <div className="flex justify-center mb-12">
                <div className="w-full"><EmbedBlock code={emb.orb_code} /></div>
              </div>
            )}
            {emb.chat_code && (
              <div className="flex justify-center mb-12">
                <div className="w-full"><EmbedBlock code={emb.chat_code} /></div>
              </div>
            )}
            {emb.phone_number && (
              <div>
                {(emb.orb_code || emb.chat_code) && (
                  <div className="flex items-center justify-center gap-4 mb-8 max-w-xs mx-auto">
                    <span className="flex-1 h-px bg-gray-200" />
                    <span className="text-[11px] uppercase tracking-[0.25em] text-gray-400 font-semibold">or</span>
                    <span className="flex-1 h-px bg-gray-200" />
                  </div>
                )}
                <a href={`tel:${emb.phone_number.replace(/[^+\d]/g, '')}`}
                  className="inline-flex items-center gap-2.5 px-7 py-4 rounded-full bg-teal-600 text-white text-sm font-semibold hover:bg-teal-500 transition-colors"
                  data-testid="proposal-call-btn">
                  <Phone size={16} /> Call our AI assistant: {emb.phone_number}
                </a>
              </div>
            )}
          </section>
        )}

        {/* Timeline */}
        {(s.deploy_timeline || []).length > 0 && (
          <section className="mb-12" data-testid="proposal-timeline">
            <h2 className="text-lg font-bold mb-5">What happens when you say yes</h2>
            <div className="space-y-0">
              {s.deploy_timeline.map((t, i) => (
                <div key={i} className="flex gap-4 pb-6 relative">
                  {i < s.deploy_timeline.length - 1 && (
                    <span className="absolute left-[15px] top-9 bottom-0 w-px bg-gray-200" />
                  )}
                  <span className="w-8 h-8 rounded-full bg-[#141821] text-white text-xs font-bold flex items-center justify-center shrink-0 z-10">{t.step}</span>
                  <div className="pt-1">
                    <p className="text-sm font-semibold">{t.title}</p>
                    <p className="text-[13px] text-gray-500 mt-0.5 leading-relaxed">{t.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Investment */}
        {(inv.upfront || inv.retainer) && (
          <section className="mb-12" data-testid="proposal-investment">
            <h2 className="text-lg font-bold mb-5">Your investment</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {inv.upfront && (
                <div className="rounded-2xl border border-black/8 bg-white p-6">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-semibold">One-time setup</p>
                  <p className="text-2xl font-bold mt-2">{inv.upfront}</p>
                </div>
              )}
              {inv.retainer && (
                <div className="rounded-2xl border-2 border-teal-600/30 bg-teal-50/50 p-6">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-teal-700 font-semibold">Monthly retainer</p>
                  <p className="text-2xl font-bold mt-2">{inv.retainer}</p>
                </div>
              )}
            </div>
            {inv.note && <p className="text-[13px] text-gray-500 mt-3">{inv.note}</p>}
            {s.roi_note && (
              <p className="text-sm text-gray-700 mt-5 border-l-2 border-teal-600 pl-4 leading-relaxed max-w-2xl">{s.roi_note}</p>
            )}
          </section>
        )}

        {/* Calendar */}
        {emb.calendar_url && (
          <section id="book" className="mb-12" data-testid="proposal-calendar">
            <h2 className="text-lg font-bold mb-2 flex items-center gap-2"><CalendarDays size={18} /> Book your call</h2>
            <p className="text-sm text-gray-600 mb-4">{s.cta_text || 'Pick a time that works for you — we’ll walk you through everything.'}</p>
            <div className="rounded-2xl border border-black/8 bg-white overflow-hidden">
              <iframe src={emb.calendar_url} title="Book a call" className="w-full" style={{ height: 680, border: 0 }} />
            </div>
          </section>
        )}

        {/* CTA footer */}
        <section className="rounded-2xl bg-[#141821] text-white p-8 text-center">
          <p className="text-lg font-bold">{s.cta_text || 'Ready to stop the leak?'}</p>
          <p className="text-sm text-white/60 mt-2">Every month of waiting costs roughly {fmt(total)}.</p>
          {emb.calendar_url && (
            <button onClick={() => document.getElementById('book')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 mt-5 px-6 py-3 rounded-full bg-teal-500 text-white text-sm font-semibold hover:bg-teal-400 transition-colors"
              data-testid="proposal-footer-schedule-btn">
              Schedule a call <ArrowRight size={14} />
            </button>
          )}
        </section>

        <p className="text-center text-[11px] text-gray-400 mt-10">
          Prepared by {data.agency?.name || 'your agency'} · Estimates based on public business data and industry benchmarks.
        </p>
      </main>
    </div>
  );
}
